import CubicGraphTool from '../MathTools/CubicGraphTool.js';
import PiecewiseGraphTool from '../MathTools/PieceWiseGraphTool.js';
import QuadraticGraphTool from '../MathTools/QuadraticGraphTool.js';
import PolynomialGraphTool from '../MathTools/PolynomialGraphTool.js';
import RationalGraphTool from '../MathTools/RationalGraphTool.js';
import NonLinearSystemTool from '../MathTools/NonLinearSystemTool.js';
import ExponentTool from '../MathTools/ExponentTool.js';
import LogarithmGraphTool from '../MathTools/LogarithmGraphTool.js';
import CompoundInterestDemo from '../MathTools/CompoundInterestDemo.js';
import RadioactiveDecayTool from '../MathTools/RadioactiveDecayTool.js';
import SinusoidTool from '../MathTools/sinusoidTool.js';
import CLTSimulatorTool from '../MathTools/CLTSimulatorTool.js';
import NormalDistributionDemo from '../MathTools/NormalDistributionDemo.js';
import ConfidenceIntervalTool from '../MathTools/ConfidenceIntervalTool.js';
import CountingTool from '../MathTools/CountingTool.js';
import MultinomialTool from '../MathTools/MultinomialTool.js';
import InclusionExclusionTool from '../MathTools/InclusionExclusionTool.js';
import PolyViewer from '../MathTools/PolyViewer.js';
import BinomialTool from '../MathTools/BinomialTool.js';
import LimitExplorerTool from '../MathTools/LimitExplorerTool.js';
import TangentSlopeTool from '../MathTools/TangentSlopeTool.js';
import CircleTool from '../MathTools/CircleTool.js';
import InverseGraphTool from "../MathTools/InverseGraphTool";
import PolarGraphTool from "../MathTools/PolarGraphTool";
import ParametricGraphTool from "../MathTools/ParametricGraphTool";
import CompositionGraphTool from "../MathTools/CompositionGraphTool";

export const chapterPages = {
    /* ----------------------------------------------------------
       CHAPTER 1: Advanced Functions & Transformations
    ---------------------------------------------------------- */
    1: [
      {
        title: <h1>Advanced Functions — Slide 1: Review of Basic Transformations</h1>,
        body: (
          <>
            <p>
              We’ll start with a quick recap of function transformations: vertical/horizontal
              shifts, stretches, and reflections. In Grade 12 we extend these to more complex
              "parent functions" like <em>cubic, radical, or absolute value</em> forms.
            </p>
            <p>
              <strong>Example:</strong> A function <code>y = f(x - 2) + 3</code> is shifted right by 2,
              up by 3. A function <code>y = -2 f(x)</code> is reflected (over the x-axis) and vertically
              stretched by factor 2.
            </p>
          </>
        )
      },
      {
        title: <h1>Advanced Functions — Slide 2: Combining Transformations</h1>,
        body: (
          <>
            <p>
              Often, we combine multiple transformations in a single function. For instance:
              <br/>
              <code>y = -2 (x + 1)^3 + 5</code> can be interpreted as:
            </p>
            <ul>
              <li>Cubic function <code>x^3</code> as the base</li>
              <li>Shifted left by 1 (<code>x + 1</code>)</li>
              <li>Reflected in the x-axis & vertically stretched by factor 2</li>
              <li>Shifted up by 5</li>
            </ul>
  
            {/* Replace the old QuadraticGraphTool with our new CubicGraphTool */}
            <CubicGraphTool
  equation="-2*(x+1)**3 + 5"
  width={500}
  height={1000}
  showAxes
  showTicks
  xTickStep={1}
  yTickStep={100}  // bigger step => far fewer y ticks
/>
            <p style={{ fontStyle: "italic" }}>
              Now we’re using a dedicated CubicGraphTool to visualize the actual cubic transformation.
            </p>
          </>
        )
      },
      {
        title: <h1>Advanced Functions — Slide 3: Piecewise Definitions</h1>,
        body: (
          <>
            <p>
              In Grade&nbsp;12, we often work with <strong>piecewise‐defined</strong> functions,
              which can have different rules in different intervals. Each “piece” can be
              transformed or styled independently, creating advanced shapes or discontinuities.
            </p>
            <p>
              <strong>Example:</strong>{" "}
              <code>
                f(x) = {"{"} x+2 for x&lt;0; &nbsp; (x-1)^2 for x≥0 {"}"}
              </code>
            </p>
            <p>
              Notice we must clarify how the boundary at <code>x=0</code> is treated:
              <ul>
                <li><code>x+2</code> applies for <code>x&lt;0</code> (so not included at x=0).</li>
                <li><code>(x-1)^2</code> applies for <code>x≥0</code> (so the boundary is included in this piece).</li>
              </ul>
              This can lead to jumps or points of continuity at x=0. Always note
              whether intervals are open (<code>&lt;</code>) or closed (<code>≤</code>).
            </p>
      
            <p>
              Below is a quick visual from a custom <code>PiecewiseGraphTool</code> that plots each piece
              in its domain:
            </p>
      
            {/* Use a new “PiecewiseGraphTool” to illustrate the example piecewise function */}
            <PiecewiseGraphTool
  pieces={[
    {equation: "x+2", xMin: -5, xMax: 0, openRight: true},
    {equation: "(x-1)**2", xMin: 0, xMax: 5, closedLeft: true}]}
  width={400}
  height={300}
  showAxes
  showTicks
  yTickStep={2}    // now we have a tick every 2 units on y
  xTickStep={1}
/>
            <p style={{ marginTop: "1em" }}>
              We see the linear segment for <code>x&lt;0</code> and the parabola for <code>x≥0</code> 
              (with an open vs. closed dot at x=0). This helps us visualize boundary behavior 
              and any jumps in the function.
            </p>
          </>
        )
      },
      {
        title: <h1>Advanced Functions — Slide 4: Function Operations &amp; Compositions</h1>,
        body: (
          <>
            <p>
              We can <em>add, subtract, multiply, and divide</em> entire functions: e.g., 
              <code>(f+g)(x) = f(x) + g(x)</code>, <code>(f·g)(x) = f(x)*g(x)</code>, 
              and so on. Another powerful idea is <strong>composition</strong>, 
              where <code>(f∘g)(x)=f(g(x))</code>. This “plugs” the output of <code>g</code> 
              into <code>f</code>, creating a single, more complex rule.
            </p>
      
            <p>
              <strong>Example:</strong> If <code>f(x)=x^2 + 1</code> and <code>g(x)=2x−1</code>, 
              then 
              <code>(f∘g)(x) = f(g(x)) = (2x−1)^2 +1</code>. 
              We’ve built a new function from the “inside out.” 
            </p>
      
            <p>
              <em>Domain &amp; Range Note:</em> In composition, the domain of <code>(f∘g)</code> 
              must respect both <code>g</code>’s domain and any restrictions on <code>f</code> 
              once <code>g(x)</code> is substituted. For instance, if <code>f</code> 
              only accepts nonnegative inputs (like <code>f(x)=√x</code>), then we must ensure 
              <code>g(x)≥0</code> for the composition to be valid. This is often crucial in advanced 
              problems involving radicals, logs, or rational expressions.
            </p>
      
            <p>
              Combining transformations with composition can yield quite intricate expressions. 
              We’ll revisit composition when discussing <strong>inverse functions</strong>, because 
              <code>f<sup>−1</sup>(f(x))=x</code> is a key property of inverses.
            </p>
      
            <p>
              Below is a quick illustration of the function 
              <code>(f∘g)(x) = (2x−1)^2 +1</code>, using our QuadraticGraphTool 
              (since it ends up being a quadratic in <code>x</code>):
            </p>
      
            {/* A mini demonstration—just to visualize the final composition */}
            <QuadraticGraphTool
              equation="(2*x - 1)**2 + 1"
              showVertex
              showRoots
              width={350}
              height={300}
              style={{ margin: "1em auto" }}
            />
      
            <p style={{ fontStyle: "italic" }}>
              Here, <code>(2x−1)</code> replaced <code>x</code> in the base <code>x^2+1</code>, 
              shifting/scaling the parabola accordingly. 
            </p>
      
            <p>
              In more advanced courses, function operations and composition arise frequently 
              in <strong>algebraic manipulation</strong>, <strong>analysis of inverses</strong>, 
              and in modeling contexts where multiple transformations or processes are chained together.
            </p>
          </>
        )
      },
      {
        title: <h1>Advanced Functions — Slide 5: Deeper Look at Inverses</h1>,
        body: (
          <>
            <p>
              A key application of function operations is finding and analyzing
              <strong> inverse functions</strong>. If <code>y = f(x)</code> is
              invertible, then its inverse <code>f<sup>−1</sup></code> satisfies
              <code>f<sup>−1</sup>(f(x))=x</code> (for all x in the domain of f).
            </p>
            <p>
              <em>Algebraic Approach:</em> to find <code>f<sup>−1</sup></code>, we often:
            </p>
            <ol>
              <li>Start with <code>y=f(x)</code>.</li>
              <li>Solve for x in terms of y.</li>
              <li>Swap x ↔ y; the “new y” is <code>f<sup>−1</sup>(x)</code>.</li>
            </ol>
            <p>
              <strong>Example:</strong> If <code>f(x)=2x+3</code>, solve y=2x+3 for x, 
              get <code>x=(y−3)/2</code>. Then swap to get <code>f<sup>−1</sup>(x)=(x−3)/2</code>.
            </p>
            <p>
              <em>Graphical Approach:</em> <code>f<sup>−1</sup></code> is the reflection
              of <code>f</code> across the line <code>y=x</code>. Checking composition:
              <code>(f∘f<sup>−1</sup>)(x)=x</code> confirms the inverse relationship.
            </p>
            <InverseGraphTool
  fEqn="x+3"
  xMin={-5}
  xMax={5}
  width={400}
  height={300}
  showLineYeqX
  showAxes
  showTicks
/>
            <p>
              In advanced courses, inverses appear in <strong>exponential &amp; logarithmic</strong>
              relationships, <strong>trigonometric &amp; inverse trig</strong> functions, 
              and more. Domain &amp; range restrictions often matter to ensure the inverse 
              is a proper function.
            </p>
          </>
        )
      },
    
      {
        title: <h1>Advanced Functions — Slide 6: Real-World Modeling via Composition</h1>,
        body: (
          <>
            <p>
              <strong>Composition</strong> also arises in real‐world modeling where multiple processes 
              occur in sequence. For example, imagine a production scenario:
            </p>
            <ul>
              <li>
                <code>g(x)</code> represents the daily output (units) as a function of workforce <code>x</code>.
              </li>
              <li>
                <code>f(u)</code> represents the profit (in \$) from selling <code>u</code> units.
              </li>
            </ul>
            <p>
              Composing them, <code>(f∘g)(x) = f(g(x))</code> gives profit as a direct function of 
              workforce, effectively chaining two relationships. Adjusting 
              <code>g</code> or <code>f</code> individually modifies the overall model 
              in a systematic way.
            </p>
            <CompositionGraphTool
  fEqn="(u)**2 +1"
  gEqn="2*x -1"
  xMin={-5}
  xMax={5}
  width={400}
  height={300}
  showAxes
  showTicks
/>
            <p>
              <em>Another Example:</em> In <strong>finance</strong>, we might see a function 
              <code>h(t)</code> that accumulates interest on an account, then a function
              <code>r(A)</code> that calculates monthly repayment based on the account’s 
              amount <code>A</code>. Composing <code>r(h(t))</code> yields the 
              “repayment as a function of time,” capturing both the accumulation process 
              and the repayment policy.
            </p>
            <p>
              Thus, <strong>function operations</strong> (including composition) provide 
              a flexible toolkit for building multi‐step models—particularly in advanced 
              math, physics, business, or engineering contexts.
            </p>
          </>
        )
      }     
    ],
  
    /* ----------------------------------------------------------
       CHAPTER 2: Polynomial Functions
    ---------------------------------------------------------- */
    2: [
        {
          title: <h1>Polynomial Functions — Slide 1: End Behavior</h1>,
          body: (
            <>
              <p>
                A <strong>polynomial</strong> is an expression of the form 
                <code> a<sub>n</sub> x^n + … + a<sub>1</sub> x + a<sub>0</sub></code>, where exponents are 
                nonnegative integers. In Grade 12, we focus on degrees up to 4 or 5 (cubic, quartic, etc.).
              </p>
              <p>
                <strong>End behavior</strong> is determined by the sign of the leading coefficient 
                <code>a<sub>n</sub></code> and whether the degree <code>n</code> is even or odd:
              </p>
              <ul>
                <li>
                  Even degree, positive leading → <code>↑↑</code> as <code>x → ±∞</code> 
                  (both ends go up).
                </li>
                <li>
                  Even degree, negative leading → <code>↓↓</code> (both ends go down).
                </li>
                <li>
                  Odd degree, positive leading → <code>↓↑</code> (left end down, right end up).
                </li>
                <li>
                  Odd degree, negative leading → <code>↑↓</code> (left end up, right end down).
                </li>
              </ul>
              <p>
                <em>Leading term test:</em> For large <code>|x|</code>, the highest‐degree term 
                <code>a<sub>n</sub> x^n</code> dominates, so that’s what dictates the “ends” of the graph.
              </p>
      
              {/* Optional small illustration with a tool 
                  that can show different polynomials quickly. 
                  For example, we might show a sample quartic or cubic. */}
              <p>
                Here’s a quick illustration of a positive cubic (<code>+x^3</code>) vs. a negative cubic (<code>-x^3</code>):
              </p>
              <div style={{ display: "flex", gap: "2rem", justifyContent: "center" }}>
                {/* If you have a "CubicGraphTool," we can show two side by side. */}
                <div>
                  <p style={{ textAlign:"center" }}><strong>y=x^3</strong></p>
                  <CubicGraphTool
                    equation="x**3"
                    xMin={-3}
                    xMax={3}
                    width={400}
                    height={400}
                    showAxes
                    showTicks
                  />
                </div>
                <div>
                  <p style={{ textAlign:"center" }}><strong>y=-x^3</strong></p>
                  <CubicGraphTool
                    equation="-(x**3)"
                    xMin={-3}
                    xMax={3}
                    width={400}
                    height={400}
                    showAxes
                    showTicks
                  />
                </div>
              </div>
              <p style={{ fontStyle: "italic", marginTop:"0.5em", textAlign:"center" }}>
                Notice how the sign of the leading coefficient changes the direction of each “end.”
              </p>
            </>
          )
        },
        {
          title: <h1>Polynomial Functions — Slide 2: Factoring &amp; Zeros</h1>,
          body: (
            <>
              <p>
                Finding real zeros of a polynomial often involves <strong>factoring</strong>:
              </p>
              <ul>
                <li>Common factors & grouping</li>
                <li>Quadratic expansions: <code>(x - r)(x - s)…</code></li>
                <li>
                  Sum/difference of cubes: <code>a^3 - b^3 = (a - b)(a^2 + ab + b^2)</code>, 
                  <code>a^3 + b^3 = (a + b)(a^2 - ab + b^2)</code>
                </li>
              </ul>
              <p>
                Each real root <code>r</code> corresponds to a factor <code>(x - r)</code>. If 
                <code>(x - r)</code> repeats, we have a repeated root, affecting how the graph 
                “touches” or “crosses” the x-axis at that point.
              </p>
              <p>
                <strong>Rational Root Theorem:</strong> If the polynomial has integer coefficients, 
                any rational zero <code>p/q</code> (in lowest terms) must have <code>p</code> dividing 
                the constant term and <code>q</code> dividing the leading coefficient. This helps 
                us guess possible roots to test.
              </p>
              <p>
                <strong>Descartes’ Rule of Signs:</strong> can give the maximum possible number 
                of positive or negative real roots by counting sign changes in <code>f(x)</code> 
                and <code>f(-x)</code>.
              </p>
              <PolynomialGraphTool
        equation="x**3 - 6*x**2 + 11*x - 6"
        displayEquation="x^3 - 6x^2 + 11x - 6"  // A nicer string to show on-screen
        xMin={-1}
        xMax={4}
        width={400}
        height={300}
        showAxes
        showTicks
        showRoots // hypothetical prop to highlight or label roots
      />
            </>
          )
        },
        {
            title: <h1>Polynomial Functions — Slide 3: Polynomial Division</h1>,
            body: (
              <>
                <p>
                  We can divide polynomials via <strong>long division</strong> or <strong>synthetic division</strong>.
                  This breaks a higher-degree polynomial into a quotient &amp; remainder:
                </p>
                <p>
                  <code>f(x) = (x - r)*q(x) + remainder</code> when dividing by <code>(x - r)</code>.
                </p>
                <p>
                  If the remainder is 0, then <code>r</code> is a root. This is the basis of the 
                  <em> Remainder Theorem</em> (i.e., <code>f(r)=0</code>) and the 
                  <em> Factor Theorem</em> (i.e., <code>(x-r)</code> is a factor).
                </p>
                <p>
                  <strong>Example (Long Division):</strong> Dividing <code>x^3 - 6x^2 + 11x - 6</code> 
                  by <code>(x -1)</code>:
                </p>
                <pre style={{ background:"#f5f5f5", padding:"0.5em" }}>
          {`(x^3 - 6x^2 + 11x - 6) ÷ (x - 1) => x^2 - 5x + 6 (remainder 0)`}
                </pre>
                <p style={{ fontStyle:"italic" }}>
                  So <code>x=1</code> is a root, and we get a quadratic factor <code>x^2 - 5x +6</code>.
                </p>
          
                <p>
                  Below are two quick plots showing:
                </p>
                <ul>
                  <li><code>f(x)= x^3 - 6x^2 + 11x - 6</code>, crossing the x-axis at <code>x=1</code></li>
                  <li><code>q(x)= x^2 - 5x + 6</code>, the quotient polynomial after factoring out <code>(x-1)</code></li>
                </ul>
                <div style={{ display:"flex", gap:"2rem", justifyContent:"center", flexWrap:"wrap" }}>
                  <div>
                    <p style={{ textAlign:"center", marginBottom:4 }}><strong>f(x)</strong></p>
                    <PolynomialGraphTool
                      equation="x**3 - 6*x**2 + 11*x -6"
                      displayEquation="x^3 - 6x^2 + 11x - 6"
                      xMin={-1}
                      xMax={4}
                      width={250}
                      height={220}
                      showAxes
                      showTicks
                      showRoots
                    />
                  </div>
                  <div>
                    <p style={{ textAlign:"center", marginBottom:4 }}><strong>q(x)</strong></p>
                    <PolynomialGraphTool
                      equation="x**2 - 5*x + 6"
                      displayEquation="x^2 - 5x + 6"
                      xMin={-1}
                      xMax={6}
                      width={250}
                      height={220}
                      showAxes
                      showTicks
                      showRoots
                    />
                  </div>
                </div>
                <p style={{ fontStyle:"italic", marginTop:"0.5em" }}>
                  Notice how factoring out <code>(x - 1)</code> reduces the cubic to a simpler quadratic. 
                  You can keep factoring <code>x^2 -5x+6 =&gt; (x-2)(x-3)</code> to see roots at x=2,3.
                </p>
              </>
            )
          },
        {
          title: <h1>Polynomial Functions — Slide 4: Example Graph (Quartic)</h1>,
          body: (
            <>
              <p>
                Here’s an example of a <strong>quartic</strong> polynomial (degree 4). 
                We can see up to 3 “turning points,” with end behavior similar to a 
                positive even degree (<code>↑↑</code>).
              </p>
              <QuadraticGraphTool
                equation="(x-1)**2 * (x+1)**2"
                displayEquation="(x-1)^2 * (x+1)^2"
                style={{ width: 400, margin: "1em auto" }}
                showVertex={false}
                showRoots
              />
              <p style={{ textAlign: "center", fontStyle: "italic" }}>
                This polynomial <code>(x - 1)^2 (x + 1)^2 = x^4 - 2x^2 + 1 </code> 
                “touches” the x-axis at x=±1 (repeated roots).
              </p>
              <p>
                <em>Max # turning points:</em> A degree <code>n</code> polynomial can have up to 
                <code> n-1</code> turning points. Notice how this quartic has up to 3.
              </p>
            </>
          )
        }
      ],
      
  
    /* ----------------------------------------------------------
       CHAPTER 3: Rational Functions
    ---------------------------------------------------------- */
    3: [
        {
          title: <h1>Rational Functions — Slide 1: Basic Form</h1>,
          body: (
            <>
              <p>
                A <strong>rational function</strong> is a ratio of two polynomials, 
                <code>p(x)/q(x)</code>. In Grade&nbsp;12, we study:
              </p>
              <ul>
                <li>
                  <strong>Vertical asymptotes</strong> or holes where <code>q(x) = 0</code>.
                </li>
                <li>
                  <strong>Horizontal/oblique (slant) asymptotes</strong> dictating end behavior.
                  <br/>
                  <em>Example rules:</em>
                  <ul style={{ listStyleType: "circle" }}>
                    <li>
                      If <code>deg(p) &lt; deg(q)</code>, horizontal asymptote at y=0.
                    </li>
                    <li>
                      If <code>deg(p) = deg(q)</code>, horizontal asymptote at ratio of leading coefficients.
                    </li>
                    <li>
                      If <code>deg(p) = deg(q) + 1</code>, we typically get a slant (oblique) asymptote.
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>Partial fractions</strong> for advanced manipulation (integration, solving equations).
                </li>
              </ul>
              <p>
                <em>Example:</em> <code>y = (x^2 - 4)/(x - 2)</code> can simplify to <code>x + 2</code> but has a hole at <code>x=2</code>.
              </p>
              <p>
                Always check <em>non-permissible values</em>: if <code>q(a)=0</code>, either there is a vertical asymptote (if the factor doesn’t cancel) or a hole (if it cancels with the numerator).
              </p>
            </>
          )
        },
        {
            title: <h1>Rational Functions — Slide 2: Graphing Examples &amp; Asymptotes</h1>,
            body: (
              <>
                <p>
                  Let’s see how different rational functions can exhibit vertical, horizontal, or
                  <em> oblique</em> (slant) asymptotes. We’ll focus on:
                </p>
                <ul>
                  <li>
                    <strong>Vertical asymptotes</strong> where the denominator = 0 (unless canceled),
                  </li>
                  <li>
                    <strong>Slant (oblique) asymptotes</strong> when the numerator’s degree is
                    exactly one higher than the denominator,
                  </li>
                  <li>
                    <strong>Horizontal asymptotes</strong> when degrees match (leading term ratio)
                    or numerator has a lesser degree.
                  </li>
                </ul>
          
                <h4>Example 1: Oblique (Slant) Asymptote</h4>
                <p>
                  Consider <code>y = (x + 1)/(x - 2)</code>. Let’s plot it:
                </p>
                <RationalGraphTool
                  equation="(x+1)/(x-2)"
                  width={400}
                  height={300}
                  xRange={[-6, 6]}
                  yRange={[-6, 6]}
                />
                <p style={{ fontStyle: "italic" }}>
                  We see a vertical asymptote at <code>x = 2</code>, and an oblique asymptote
                  at <code>y = 1</code>. (Dividing out: \((x+1)\div(x-2) = 1 + 3/(x-2)\).)
                </p>
          
                <h4>Example 2: Horizontal Asymptote</h4>
                <p>
                  Now consider <code>y = (3x + 2)/(5x - 1)</code>. Since the degrees match
                  (both linear), the horizontal asymptote is the ratio of leading coefficients,
                  <code>3/5</code>. (No slant line here!)
                </p>
                <RationalGraphTool
                  equation="(3*x + 2)/(5*x - 1)"
                  width={400}
                  height={300}
                  xRange={[-6, 6]}
                  yRange={[-6, 6]}
                />
          
                <p>
                  <strong>Try It:</strong> Enter other rational expressions (for instance, ones with
                  <code>x^2</code> in the numerator) in the tool’s code to see whether you get
                  horizontal or slant asymptotes, holes (factor cancellation), or multiple vertical
                  asymptotes. 
                </p>
          
                <h4>Real-World Angle</h4>
                <p>
                  In <em>cost/revenue models</em>, or <em>mixture/time</em> problems, denominators
                  can’t be zero, so vertical asymptotes represent “impossible” input values. Meanwhile,
                  a horizontal or slant asymptote often says “the function levels off (or trends) for
                  large |x|.” For instance, average cost might flatten to some constant as production
                  grows.
                </p>
              </>
            )
          },
        {
            title: <h1>Exponential & Logarithms — Slide 3: Solving Single Exponential Equations</h1>,
            body: (
              <>
                <p>
                  Many practical equations set an exponential expression equal to a constant or
                  another simpler function. If we can't solve by direct rewriting, we might
                  confirm or approximate solutions graphically.
                </p>
                <p>
                  <strong>Example:</strong> Solve <code>2^(x+1) = 5</code>. Then 
                  <code>x+1 = log<sub>2</sub>(5)</code> ⇒ <code>x = log<sub>2</sub>(5) - 1</code>,
                  which is about <code>x ≈ 1.32</code>.
                </p>
                <p>
                  Below, we plot both sides: <code>y = 2^(x+1)</code> and <code>y=5</code>, 
                  seeing their intersection near x=1.32:
                </p>
                <NonLinearSystemTool
                  eqn1="y=2^(x+1)"
                  eqn2="y=5"
                  xRange={[-2, 4]}
                  yRange={[0, 12]}
                />
                <p style={{ fontStyle:"italic" }}>
                  In Chapter 7, we’ll go further with advanced systems (like polynomials + exponentials). 
                  Here, we’re simply verifying the single solution x ≈ 1.32.
                </p>
              </>
            )
          },
        {
          title: <h1>Rational Functions — Slide 4: Applications</h1>,
          body: (
            <>
              <p>
                <strong>Rational models</strong> appear frequently in real‐world contexts:
              </p>
              <ul>
                <li>
                  <em>Rates &amp; Mixture Problems:</em> e.g. combined work rates or solution concentrations can lead to equations with denominators based on sum of times or volumes.
                </li>
                <li>
                  <em>Cost–Revenue–Profit:</em> dividing total cost by number of units, or modeling average cost often yields rational expressions with domain restrictions (like x&gt;0).
                </li>
                <li>
                  <em>Asymptotes in Real Data:</em> e.g. diminishing returns, “leveling off” behavior as x→∞.
                </li>
              </ul>
              <p>
                In all these, watch for <strong>domain restrictions</strong> (where denominators vanish) and interpret vertical/horizontal asymptotes in practical terms (like unattainable production or limiting behavior).
              </p>
              <p>
  <strong>Example (Average Cost):</strong> 
  Suppose fixed cost = \$100, variable cost = \$5 per unit. 
  Then <code>AC(x)= (100+5x)/x</code>, 
  domain <code>x&gt;0</code>.
</p>
<RationalGraphTool
  equation="(100 + 5*x)/x"
  width={800}
  height={600}
  xRange={[-10, 10]}
  yRange={[-200, 200]}
  showAxes
  showTicks
/>
<p style={{ fontStyle:"italic" }}>
  As <code>x→∞</code>, the cost approaches \$5 (horizontal asymptote). 
  We also see a vertical asymptote at <code>x=0</code>.
</p>

            </>
          )
        }
      ],
      
  
    /* ----------------------------------------------------------
       CHAPTER 4: Exponential & Logarithmic Functions
    ---------------------------------------------------------- */
    4: [
        // SLIDE 1 -------------------------------------
        {
            title: <h1>Exponential &amp; Logarithms — Slide 1: Properties Review</h1>,
            body: (
              <>
                <p>
                  An <strong>exponential</strong> function has the form <code>a<sup>x</sup></code>. 
                  Its inverse is a <strong>logarithm</strong>: 
                  <code>y = log_a(x)</code> means <code>a<sup>y</sup> = x</code>.
                </p>
                <p>
                    Try our <strong>ExponentialGraphTool</strong> below to see how changing the base or 
                    applying shifts (x−h, +k) modifies the curve.
                </p>
                <ExponentTool defaultBase={2} allowShift/>
                <p>
                  Key properties (for a &gt; 0, a ≠ 1):
                  <ul>
                    <li><code>{"log_a(M*N) = log_a(M) + log_a(N)"}</code> (product rule)</li>
                    <li><code>{"log_a(M^k) = k * log_a(M)"}</code> (power rule)</li>
                    <li><code>{"log_a(M/N) = log_a(M) - log_a(N)"}</code> (quotient rule)</li>
                    <li>
                      <strong>Change of base formula:</strong> 
                      <code>{"log_a(x) = log_b(x) / log_b(a)"}</code>.
                    </li>
                  </ul>
                </p>
                <p>
                  Also remember negative exponents (<code>a<sup>-x</sup> = 1 / a<sup>x</sup></code>) and 
                  fractional exponents (<code>a<sup>1/2</sup> = sqrt(a)</code>) follow the same rules.
                </p>
                <LogarithmGraphTool
                  initialBase={2}
                  xMin={0.1}
                  xMax={10000}
                  width={1500}
                  height={800}
                />
                <p style={{ fontStyle:"italic" }}>
                  Here we see how the log curve changes with base. 
                </p>
              </>
            )
          },
          {
            title: <h1>Exponential & Logarithms — Slide 3: Solving Equations</h1>,
            body: (
              <>
                <p>Many advanced equations combine exponentials &amp; logs. We use:</p>
                <ul>
                  <li><strong>Log properties</strong> … </li>
                  <li><strong>Exponent rules</strong> … </li>
                  <li><strong>Numerical methods</strong> … </li>
                </ul>
          
                <p>
                  <strong>Example:</strong> Solve <code>2^(x+1) = 5</code>. Then 
                  <code>x+1 = log<sub>2</sub>(5)</code> ⇒ <code>x = log<sub>2</sub>(5) - 1</code>,
                  which is about <code>x ≈ 1.32</code>.
                </p>
          
                <p>Below, a mini‐plot of both sides; their intersection is the solution:</p>
                <NonLinearSystemTool
                  eqn1="y=2^(x+1)"
                  eqn2="y=5"
                  xRange={[-2, 4]}
                  yRange={[0, 12]}
                />
                <p style={{ fontStyle:"italic" }}>
                  You can see the intersection around x=1.32, matching our \(\log_2(5)-1\) calculation.
                </p>
              </>
            )
          },
          {
            title: <h1>Exponential &amp; Logarithms — Slide 4: Real-World Applications</h1>,
            body: (
              <>
                <p>
                  Exponential/log behavior appears widely in finance (compound interest), biology (population 
                  growth), physics (radioactive decay), pH logs in chemistry, and more.
                </p>
                <p>
                  <strong>Compound Interest Example:</strong><br/>
                  <code>A = P(1 + r/n)^(n*t)</code> grows exponentially over time. Taking logs helps solve
                  for time <code>t</code> to reach amount <code>A</code>.
                </p>
                <CompoundInterestDemo
                  principal={1000}
                  rate={0.05}
                  years={10}
                  width={2000}
                  height={250}
                />
        
                <p style={{ fontStyle:"italic", textAlign:"center" }}>
                  This demonstration, if available, shows how principal grows exponentially 
                  with repeated compounding, and how logs help solve for doubling time.
                </p>
                <p>
                  Similarly, <strong>Radioactive Decay</strong> follows 
                  <code>N(t) = N_0 * e^(-k t)</code>, 
                  and <strong>Half‐life</strong> solves <code>N(t)=N_0/2</code> with logs.
                </p>
                <RadioactiveDecayTool
                initialN0={1000}
                 initialK={0.05}
                    timeMax={50}
                     width={400}
                    height={250}
                    />
              </>
            )
          }
      ],
      
  
    /* ----------------------------------------------------------
       CHAPTER 5: Trigonometric Identities
    ---------------------------------------------------------- */
    5: [
        {
          title: <h1>Trigonometric Identities — Slide 1: Fundamental Identities</h1>,
          body: (
            <>
              <p>
                In Grade&nbsp;12, we delve more deeply into the classic 
                <strong> Pythagorean identities</strong>:
              </p>
              <ul>
                <li>
                  <code>sin²(x) + cos²(x) = 1</code>
                </li>
                <li>
                  <code>1 + tan²(x) = sec²(x)</code>
                </li>
                <li>
                  <code>1 + cot²(x) = csc²(x)</code> (sometimes used less often)
                </li>
              </ul>
      
              <p>
                These come directly from the geometry of the **unit circle**, relating the sides of 
                right triangles (adjacent, opposite, hypotenuse). They are fundamental for
                rewriting or simplifying almost any advanced trig expression.
              </p>
      
              <p>
                We also use <strong>angle sum/difference</strong> formulas, which let us
                handle <code>sin(A ± B)</code> and <code>cos(A ± B)</code>:
              </p>
              <ul>
                <li><code>sin(A ± B) = sinA·cosB ± cosA·sinB</code></li>
                <li><code>cos(A ± B) = cosA·cosB ∓ sinA·sinB</code></li>
              </ul>
              <p>
                Such formulas are crucial for solving or simplifying trig equations with sums of angles,
                or for exact values of <code>sin(75°)</code>, <code>cos(15°)</code>, etc.
              </p>
            </>
          )
        },
        {
          title: <h1>Trigonometric Identities — Slide 2: Double‐Angle &amp; Half‐Angle</h1>,
          body: (
            <>
              <p>
                <strong>Double‐angle identities</strong> are a special case of the sum formulas
                (<code>A=B</code>), giving:
              </p>
              <ul>
                <li><code>sin(2x) = 2 sin(x) cos(x)</code></li>
                <li>
                  <code>cos(2x) = cos²(x) - sin²(x)</code> &nbsp; which can also be written as <br/>
                  <code>cos(2x) = 1 - 2 sin²(x)</code> or <code>cos(2x) = 2 cos²(x) - 1</code>.
                </li>
              </ul>
              <p>
                These let us rewrite powers of sine or cosine, or quickly handle expressions like 
                <code>sin²(x)</code> in advanced proofs.
              </p>
      
              <p>
                <strong>Half‐angle identities</strong> (e.g. <code>sin(x/2)</code>, <code>cos(x/2)</code>) 
                come in handy for further solving, though they usually appear in more advanced contexts or
                when we reduce integrals in calculus.
              </p>
      
              {/*
                Optional: Show how to check sin(2x)=2sinx cosx using NonLinearSystemTool
                by letting eqn1 => y=sin(2x), eqn2 => y=2sinx cosx, see the intersection "all x".
                But that's just an idea. 
              */}
            </>
          )
        },
      
        {
          title: <h1>Trigonometric Identities — Slide 3: Proving &amp; Simplifying</h1>,
          body: (
            <>
              <p>
                We often prove an identity by rewriting one side *entirely* in terms of sin and cos 
                until it matches the other side. A typical approach is:
              </p>
              <ol>
                <li>Pick the more “complicated” side to start.</li>
                <li>Convert everything to sin/cos (if needed).</li>
                <li>Use algebraic manipulations (factoring, common denominators) and known identities.</li>
                <li>Reach the simpler side, or a known form (like 1, sin(2x), etc.).</li>
              </ol>
      
              <p>
                <strong>Example:</strong> Show that <code>tan(x) + sec(x) = sin(x)/(1 - cos(x))</code>. 
                By writing <code>tan(x)=sin(x)/cos(x)</code> and <code>sec(x)=1/cos(x)</code>, 
                you can combine them over a common denominator, then factor in the numerator, etc.
              </p>
      
              <CircleTool
                circleRadius={4}
                width={300}
                height={300}
                style={{ margin: "1em auto" }}
              />
              <p style={{ textAlign: "center", fontStyle: "italic" }}>
                A reference circle helps visualize how <code>sin(θ)</code> and <code>cos(θ)</code> 
                relate to coordinates on the unit circle, and how <code>tan(θ)</code>, <code>sec(θ)</code>, etc. can be interpreted geometrically.
              </p>
            </>
          )
        },
      
        {
          title: <h1>Trigonometric Identities — Slide 4: Solving Advanced Trig Equations</h1>,
          body: (
            <>
              <p>
                Once you’re comfortable manipulating these identities, you can tackle more
                advanced trig equations:
              </p>
              <ul>
                <li><strong>Unify sin/cos into one function</strong> if possible (e.g. using <code>sin²(x) + cos²(x)=1</code> to replace one of them).</li>
                <li>
                  <strong>Factor or isolate angles</strong> (sometimes you rearrange to a polynomial in 
                  <code>sin(x)</code> or <code>cos(x)</code>).
                </li>
                <li>
                  <strong>Check extraneous solutions</strong> if you square both sides, as this can
                  introduce spurious answers.
                </li>
              </ul>
      
              <p>
                Building a robust “trig toolbox” is key. You combine fundamental 
                identities, sum/difference, double‐angle, and possibly half‐angle formulas 
                to navigate any expression. 
              </p>
      
              <p>
                <em>Example:</em> Solve <code>cos(2x)=sin(x)</code>. One approach:
                <br/>
                Use <code>cos(2x)=1 - 2 sin²(x)</code>, so <code>1 - 2 sin²(x)= sin(x)</code>.
                <br/>
                Rearrange to <code>2 sin²(x) + sin(x) -1=0</code>, set <code>u= sin(x)</code>, solve
                <code>2u² + u -1=0</code>, etc. Then check valid angles from the solutions.
              </p>
            </>
          )
        }
      ],
      
  
    /* ----------------------------------------------------------
       CHAPTER 6: Sinusoidal Functions
    ---------------------------------------------------------- */
    // Enhanced Chapter 6 Slides

6: [
    {
      title: <h1>Sinusoidal Functions — Slide 1: General Form</h1>,
      body: (
        <>
          <p>
            A general sinusoid can be written as <code>y = A&#160;sin&#40;B&#40;x&#160;−&#160;C&#41;&#41;&#160;+&#160;D</code> 
            or <code>y = A&#160;cos&#40;B&#40;x&#160;−&#160;C&#41;&#41;&#160;+&#160;D</code>, where:
          </p>
          <ul>
            <li>
              <strong>A</strong> = amplitude (the “height” from midline to peak)
            </li>
            <li>
              <strong>Period</strong> = <code>2π&#47;B</code> (the length of one full cycle)
            </li>
            <li>
              <strong>C</strong> = phase shift (horizontal shift)
            </li>
            <li>
              <strong>D</strong> = vertical shift (the midline)
            </li>
          </ul>
          <p>
            Sinusoids unify both <em>sine</em> and <em>cosine</em> waves, plus transformations. 
            They appear in any cyclical phenomenon—tides, waves, seasonal patterns, etc.
          </p>
        </>
      )
    },
    {
      title: <h1>Sinusoidal Functions — Slide 2: Graphing Example</h1>,
      body: (
        <>
          <p>
            Below is a <em>real</em> sinusoidal exploration tool. You can see how changing <em>A</em> 
            (amplitude), <em>B</em> (which affects period), <em>C</em> (horizontal shift), and <em>D</em> 
            (vertical shift) changes the sine wave:
          </p>
          {/* If you have a dedicated Sine wave tool, embed it here. Example: SinusoidTool */}
          <SinusoidTool
            initialA={1}
            initialB={1}
            initialC={0}
            initialD={0}
            width={500}
            height={300}
          />
          <p style={{ fontStyle: "italic", marginTop: "1em" }}>
            The above tool plots 
            <code> y = A · sin [ B ( x − C ) ] + D</code>.
            Move each slider to see the effect on amplitude, period, horizontal shift, and midline.
          </p>
        </>
      )
    },
    {
      title: <h1>Sinusoidal Functions — Slide 3: Applications</h1>,
      body: (
        <>
          <p>
            Sinusoids model any repeating or oscillating behavior. Common examples:
          </p>
          <ul>
            <li>Tidal heights over time</li>
            <li>Seasonal temperature (hot–cold) patterns</li>
            <li>AC electricity voltage waveforms</li>
            <li>Sound waves, pendulum motion, etc.</li>
          </ul>
          <p>
            When analyzing a real dataset (e.g. temperature vs. day), we often identify:
          </p>
          <ul>
            <li><strong>Amplitude</strong>: how far above (or below) the midline we oscillate</li>
            <li><strong>Period</strong>: the time or “width” of one complete cycle</li>
            <li><strong>Phase shift</strong>: where the cycle starts horizontally</li>
            <li><strong>Vertical shift</strong> (midline): the baseline or average level</li>
          </ul>
        </>
      )
    },
    {
      title: <h1>Sinusoidal Functions — Slide 4: Phase Shift &amp; Inverse Trig</h1>,
      body: (
        <>
          <p>
            We can also <strong>invert</strong> sinusoids—finding <em>arcsin</em>, <em>arccos</em>, or 
            <em>arctan</em>—provided we restrict the domain to make the function one-to-one. 
            This is key to solving equations like <code>sin(θ)=0.75</code> or
            <code>cos(θ)=−0.3</code>.
          </p>
          <p>
            We may also combine transformations. For example, 
            <code>y = 4 cos&#40;2(x−π/4)&#41; − 2</code> means:
          </p>
          <ul>
            <li>Amplitude 4 (from the “4” in front)</li>
            <li>Period <code>π</code> (since <code>B=2</code>, so period = <code>2π / 2 = π</code>)</li>
            <li>Horizontal shift <code>π/4</code> to the right</li>
            <li>Vertical (midline) shift <code>D=−2</code></li>
          </ul>
          <p>
            This all follows from the same transformations logic we studied: 
            scaling horizontally by factor <code>1/B</code>, shifting by <code>C</code>, 
            stretching vertically by <code>A</code>, and shifting vertically by <code>D</code>.
          </p>
        </>
      )
    }
  ],
  
  
    /* ----------------------------------------------------------
   /* ----------------------------------------------------------
   CHAPTER 7: Advanced Equations (Systems, Nonlinear)
---------------------------------------------------------- */
7: [
    {
      title: <h1>Advanced Equations — Slide 1: Higher‐Degree & Mixed Systems</h1>,
      body: (
        <>
          <p>
            In Grade&nbsp;12, we often encounter systems (or single equations) where at least one
            equation is:
          </p>
          <ul>
            <li>
              A higher‐degree polynomial (cubic, quartic, etc.),
            </li>
            <li>
              An exponential function (e.g. <code>3^x</code> or <code>10^x</code>),
            </li>
            <li>
              A logarithmic function (e.g. <code>ln(x)</code> or <code>log<sub>2</sub>(x)</code>).
            </li>
          </ul>
          <p>
            These can arise in real‐world models or pure algebraic challenges. Unlike simpler linear or
            quadratic systems, these usually do <strong>not</strong> allow neat factorization or direct
            algebraic solutions once degree ≥3 or exponentials/logs appear.
          </p>
          <p>
            <strong>Example:</strong> Solve the system
            <br />
            <code>y = x^3 - 2x + 1</code> and <code>y = 3^x - 4</code>.
            Because one is a cubic polynomial and the other is an exponential, there’s typically no
            straightforward “factoring” approach. We rely on{' '}
            <strong>numerical or graphical methods</strong> to approximate intersection points.
          </p>
        </>
      )
    },
    {
      title: <h1>Advanced Equations — Slide 2: Graphical Approaches</h1>,
      body: (
        <>
          <p>
            A powerful way to handle these complicated setups is to <em>graph each equation</em> and
            look for intersection(s). Modern calculators or software can approximate solutions
            quickly. Our <strong>NonLinearSystemTool</strong> does just that:
          </p>
          <NonLinearSystemTool />
          <p style={{ textAlign: 'center', fontStyle: 'italic' }}>
            Try customizing the example: set one equation to “cubic” (like <code>x^3 - 2x + 1</code>)
            and the other to “exponential” (<code>3^x - 4</code>) to see approximate solutions.
          </p>
          <p>
            If the tool reports “no real solutions” or decimal approximations only, that’s normal: we
            often <strong>cannot</strong> get a neat closed‐form answer.
          </p>
        </>
      )
    },
    {
      title: <h1>Advanced Equations — Slide 3: Log–Poly or Exp–Poly Mixes</h1>,
      body: (
        <>
          <p>
            Some of the most common tricky forms involve a <strong>polynomial vs. log or exponential</strong>,
            such as:
          </p>
          <ul>
            <li>
              <code>x^2 = 10^x</code> (polynomial = exponential),
            </li>
            <li>
              <code>ln(x+2) = x</code> (logarithm = linear),
            </li>
            <li>
              <code>e^x = x^3 - 1</code>, etc.
            </li>
          </ul>
          <p>
            Because these functions differ fundamentally, there’s usually <strong>no algebraic
            factorization</strong>. We rely on:
          </p>
          <ul>
            <li>Graphical intersection or numeric methods (e.g. Newton’s method),</li>
            <li>
              Occasionally, the Lambert W function (beyond typical Grade 12 scope) to solve certain
              forms.
            </li>
          </ul>
          <p>
            <em>Key takeaway:</em> No “factoring trick” is straightforward—graphical or numeric
            solutions are the practical approach in these log–poly or exp–poly mixes.
          </p>
        </>
      )
    },
    {
        title: <h1>Advanced Equations — Slide 4: Parametric or Polar Systems (Enrichment)</h1>,
        body: (
          <>
            <p>
              In more advanced courses, you might see <strong>parametric</strong> forms <code>x(t), y(t)</code> or
              <strong> polar</strong> forms <code>r = f(θ)</code>. Solving for intersections typically uses
              numeric/graphical approaches, just like with other non‐factorable equations.
            </p>
      
            <p>
              <strong>Parametric example:</strong> <code>x(t) = t^2 - 1</code>, <code>y(t) = 2t</code>.
              We can plot for, say, <code>t</code> in [−2..2]. That might be compared against a function{' '}
              <code>y = f(x)</code> or another parametric set <code>x(s), y(s)</code> to find intersections.
            </p>
      
            {/* Hypothetical parametric grapher */}
            <ParametricGraphTool
              xEqn="t^2 - 1"
              yEqn="2*t"
              tMin={-2}
              tMax={2}
              steps={100}
              width={300}
              height={250}
            />
      
            <p>
              <strong>Polar example:</strong> <code>r = 2 + cos(θ)</code> might be intersected with{' '}
              <code>r = 4 sin(θ)</code>. Each describes a curve in polar coordinates. We could plot both
              simultaneously and look for intersection angles <code>θ</code>.
            </p>
      
            {/* Hypothetical polar grapher */}
            <PolarGraphTool
              equations={[
                { rEqn: '2 + cos(theta)', color: 'blue' },
                { rEqn: '4 * sin(theta)', color: 'red' }
              ]}
              thetaMin={0}
              thetaMax={2*Math.PI}
              steps={300}
              width={300}
              height={250}
            />
      
            <p style={{ marginTop: '1em' }}>
              Notice how you still rely on <em>numeric/graphical</em> approaches: if there’s no direct
              algebraic method to solve <code>t^2 - 1 = f(t)</code> or <code>(2 + cos(θ)) = 4 sin(θ)</code>,
              we approximate or interpret from the plotted curves.
            </p>
          </>
        )
      }  
  ],
  
  
    /* ----------------------------------------------------------
       CHAPTER 8: Combinatorics
    ---------------------------------------------------------- */
    /* ---------------- Grade 12 ‧ Topic 8: Combinatorics (English only) ---------------- */
8: [
    /* ---------- Slide 1 ---------------------------------------------------------- */
    {
      title: <h1>Combinatorics — Slide 1: Advanced Permutations</h1>,
      body: (
        <>
          <p>In Grade 12 we move past basic <code>nPk</code> and explore permutations with constraints:</p>
          <ul>
            <li><strong>Repeated elements (multinomial permutations):</strong>&nbsp;
                <code>n! / (n₁! n₂! …)</code></li>
            <li><strong>Circular permutations:</strong>&nbsp;
                <code>(n − 1)!</code></li>
            <li><strong>Derangements (!n):</strong>&nbsp;
                permutations with no item in its original position (≈ <code>n!/e</code>)</li>
          </ul>
          {/* CountingTool needs the extra flags shown below */}
          <CountingTool
            showPermutations
            showRepeated
            showCircular
            showDerangements
          />
          <p style={{ fontStyle: 'italic', textAlign: 'center' }}>
            Move the sliders for <code>n</code> and toggle constraints to see how the count changes.
          </p>
        </>
      )
    },
  
    /* ---------- Slide 2 ---------------------------------------------------------- */
    {
        title: <h1>Combinatorics — Slide 2: The Multinomial Theorem</h1>,
        body: (
          <>
            {/* ❶ Formal statement */}
            <p>
              <code>
                (x₁ + x₂ + … + x<sub>m</sub>)<sup>n</sup> = 
                ∑<sub>n₁+…+n<sub>m</sub>=n</sub>
                C(n;&nbsp;n₁,…,n<sub>m</sub>)
                &nbsp;x₁<sup>n₁</sup> … x<sub>m</sub><sup>n<sub>m</sub></sup>
              </code>
            </p>
      
            {/* ❷ Key facts */}
            <ul>
              <li>
                When <strong>m = 2</strong> we recover the familiar&nbsp;
                <em>binomial</em> theorem: <code>C(n,k)=n!/k!(n-k)!</code>.
              </li>
              <li>
                <strong>Multinomial coefficient</strong>&nbsp;
                <code>C(n;&nbsp;n₁,…,n<sub>m</sub>) = n! / (n₁! … n<sub>m</sub>!)</code>
              </li>
              <li>
                Combinatorial meaning: ways to place <code>n</code> distinguishable
                balls into <code>m</code> labelled boxes with exactly
                <code>&nbsp;n<sub>i</sub></code> balls in box&nbsp;<em>i</em>.
              </li>
            </ul>
      
            {/* ❸ Concrete worked example */}
            <p>
              <em>Example (m = 3, n = 4):</em>&nbsp;
              Expand <code>(a + b + c)<sup>4</sup></code> → 
              there are 15 distinct terms because the non-negative
              solutions to <code>n₁+n₂+n₃=4</code> total&nbsp;15.
              <br />
              One term, say
              <code>&nbsp;4!/(2!1!1!) · a²b c</code>,
              counts the ways of picking the positions of two <code>a</code>’s,
              one <code>b</code>, one <code>c</code>.
            </p>
      
            {/* ❹ Interactive tool */}
            <MultinomialTool m={3} n={6} />
      
            {/* ❺ Probability connection */}
            <p style={{ fontStyle: 'italic', marginTop: 4 }}>
              If each category is equally likely
              (<code>pᵢ = 1/m</code>), the same coefficient gives the probability of
              observing the split&nbsp;
              <code>(n₁,…,n<sub>m</sub>)</code>
              in <code>n</code> independent trials (multinomial distribution).
              Drag the counts above to see how the probability shrinks for
              “unbalanced” outcomes.
            </p>
      
            {/* ❻ Why this matters */}
            <ul style={{ listStyle: 'square', marginTop: 6 }}>
              <li>Counting colourings of dice faces or card hands with suits.</li>
              <li>Coefficients in the expansion of generating functions.</li>
              <li>Foundation for Pearson’s χ² goodness-of-fit test in statistics.</li>
            </ul>
          </>
        )
      },
  
    /* ---------- Slide 3 ---------------------------------------------------------- */
    {
      title: <h1>Combinatorics — Slide 3: Inclusion–Exclusion Principle</h1>,
      body: (
        <>
          <p>
            The <strong>Inclusion–Exclusion Principle</strong> corrects over-counting when sets overlap:
            <br />
            <code>|A ∪ B ∪ C| = |A| + |B| + |C| − |A ∩ B| − |A ∩ C| − |B ∩ C| + |A ∩ B ∩ C|</code>
          </p>
          <p>
            Example problem: 5 distinct books are placed into 3 drawers. Drawer 1 cannot contain Book A,
            Drawer 2 cannot contain Book B, Drawer 3 cannot contain Book C.
            How many valid ways exist?
          </p>
          {/* Replace with a static diagram if InclusionExclusionTool is not yet implemented */}
          <InclusionExclusionTool sets={3} />
          <p style={{ fontStyle: 'italic' }}>
            Toggling the forbidden conditions instantly updates the count.
          </p>
        </>
      )
    },
  
    /* ---------- Slide 4 ---------------------------------------------------------- */
    {
        title: <h1>Combinatorics — Slide 4: Generating Functions ⇄ Probability</h1>,
        body: (
          <>
            {/* ❶ Ordinary GF definition */}
            <p>
              An <strong>ordinary generating function (GF)</strong> packages a sequence
              <code>&nbsp;(a₀,a₁,…)</code> as
              <code> G(x)=Σ a<sub>k</sub>x<sup>k</sup></code>.
              <br />
              <em>Convolution of sequences ↔ multiplication of their GFs.</em>
            </p>
      
            {/* ❷ Dice example with a tiny visual polynomial viewer */}
            <p style={{ marginBottom: 4 }}>
              <em>Example 1 – summing two dice</em>  
              &nbsp;GF for a single fair die:
              &nbsp;<code>x + x² + … + x⁶</code>. &nbsp;
              Squaring it gives the coefficients of the 2-dice sum distribution.
            </p>
            <PolyViewer poly="x + x^2 + x^3 + x^4 + x^5 + x^6" power={2} />  {/* optional helper */}
      
            {/* ❸ Binomial GF -> BinomialTool */}
            <p>
              <em>Example 2 – repeated Bernoulli trials</em><br />
              For a single trial with success-probability <code>p</code> we have
              <code>&nbsp;G₁(x)=q + p x</code> where <code>q = 1−p</code>.
              Raising to the <code>n</code>-th power,
              <code>&nbsp;(q + p x)<sup>n</sup></code>,
              the coefficient of <code>x<sup>k</sup></code> is
              <code>C(n,k)p<sup>k</sup>q<sup>n−k</sup></code>—exactly the binomial PMF.
            </p>
      
            {/* ❹ Interactive link */}
            <BinomialTool n={10} p={0.3} showPdf showCdf />
            <p style={{ fontStyle: 'italic', marginTop: 4 }}>
              Move <code>n</code> or <code>p</code>.  
              You are directly changing the generating function
              <code>&nbsp;(q + p x)<sup>n</sup></code> and watching its coefficients
              (probabilities) update in real time.
              Large <code>n</code> makes the histogram bell-shaped—foreshadowing the
              Central Limit Theorem.
            </p>
          </>
        )
      }
  ],
  
  
    /* ----------------------------------------------------------
       CHAPTER 9: Probability & Statistics
    ---------------------------------------------------------- */
    /* ---------------- Grade 12 ‧ Chapter 9: Probability & Statistics ------------- */
/* ---------------- Grade 12 ‧ Chapter 9: Probability & Statistics ----------- */
9: [
    /* ──────────────────────────────────────────────────────────────────────── */
    {
      title: <h1>Probability & Statistics — Slide 1: From Discrete to Continuous</h1>,
      body: (
        <>
          <p>
            We generalise the discrete distributions you met in Grade&nbsp;11
            (binomial, hyper-geometric, Poisson) to <em>continuous</em> models—
            most famously the <strong>normal (Gaussian) distribution</strong>.
            The bridge is the <strong>Central Limit Theorem&nbsp;(CLT)</strong>:
            sums/means of many i.i.d.&nbsp;variables converge in shape to a normal curve.
          </p>
  
          <ul>
            <li>
              <strong>Binomial (n,&nbsp;p)</strong> with large&nbsp;<em>n</em> and neither&nbsp;<em>p </em>
              nor&nbsp;<code>1-p</code> too small
              &nbsp;<span style={{ fontSize: 13 }}>(np&nbsp;&gt;&nbsp;5 &amp; n(1-p)&nbsp;&gt;&nbsp;5)</span>
              ≈ <strong>Normal</strong>
              {`(μ = np, σ = √(np(1−p)))`}.
            </li>
            <li>
              <strong>Other key continuous laws:</strong> Student-<em>t</em>, χ², F
              —​used later for inference tests.
            </li>
          </ul>
        </>
      )
    },
  
    /* ─────────────────────────────────────────────────────────────────────── */
{
    title: <h1>Probability & Statistics — Slide 2: Central Limit Theorem in Action</h1>,
    body: (
      <>
        <p>
          The <strong>Central Limit Theorem</strong> says the <em>sampling mean</em> of
          any i.i.d. variable approaches a normal distribution as the sample
          size <em>n</em> grows.
        </p>
  
        <CLTSimulatorTool n={2} N={1000} />
  
        <p style={{ fontStyle: 'italic', marginTop: 6 }}>
          Start with n = 1 (uniform histogram). Increase n; the bars rapidly morph
          into the bell curve — empirical proof of the CLT.
        </p>
      </>
    )
  },
  
  /* ─────────────────────────────────────────────────────────────────────── */
  {
    title: <h1>Probability & Statistics — Slide 3: z-Scores & Standardisation</h1>,
    body: (
      <>
        <p>
          Any normally distributed variable <em>X ∼ N(μ, σ)</em> can be converted
          to the <strong>standard normal</strong> with&nbsp;
          <code>{`z = (x − μ) / σ`}</code>.  This lets us compare values across
          different normal datasets and use one universal <em>z-table</em>.
        </p>
  
        <NormalDistributionDemo mean={0} stdDev={1} showCurve showZSlider />
  
        <p style={{ fontStyle: 'italic', marginTop: 4 }}>
          Drag the z-slider → shaded area updates and the tool reports&nbsp;
          <code>{`P(Z ≤ z)`}</code>.
        </p>
      </>
    )
  },
  
    /* ──────────────────────────────────────────────────────────────────────── */
    {
      title: <h1>Probability & Statistics — Slide 4: Normal ≈ Binomial (Live Approx.)</h1>,
      body: (
        <>
          <p>
            For large <em>n</em>, a binomial histogram “smooths out.”&nbsp;
            Add the <strong>continuity correction</strong> (±0.5) and the normal
            curve overlays neatly.
          </p>
  
          {/* overlay tool: assumes BinomialTool supports showNormalOverlay */}
          <BinomialTool
        n={15}
        p={0.3}
        showPdf
        showNormalOverlay
        showContinuityBands   /* new */
        bandAlpha={0.15}      /* optional opacity */
        />
          <p style={{ fontSize: 12, fontStyle: 'italic' }}>
            Increase <em>n</em> and watch the blue bars hug the orange normal
            curve—real-time evidence of the CLT.
          </p>
        </>
      )
    },
    {
        title: <h1>Probability &amp; Statistics — Slide&nbsp;5: Confidence Intervals &amp; Tests</h1>,
        body: (
          <>
            <p>
              Once the normal (or Student-<em>t</em>) model is justified for
              a sample mean <code>{`x̄`}</code>, we can compute a
              <strong>confidence interval&nbsp;(CI)</strong> and run a
              <strong>one-sample <em>t</em> test</strong>.
            </p>
      
            <ul>
              <li>
                <em>CI for μ (σ unknown):</em>&nbsp;
                <code>{`x̄ ± t_{α/2, n−1} · s / √n`}</code>
              </li>
              <li>
                <em>Example numbers:</em>&nbsp;n = 25,&nbsp;<code>x̄ = 72</code>, s = 8
                → 95 % CI ≈ (68.7,&nbsp;75.3).
              </li>
              <li>
                Two-tailed test H<sub>0</sub>: μ = 70 → reject if 70 lies
                **outside** the CI.
              </li>
            </ul>
      
            {/* NEW interactive illustration */}
            <ConfidenceIntervalTool
              n={25}
              mean={72}
              s={8}
              mu0={70}
              confidence={0.95}
            />
      
            <p style={{ fontStyle:'italic' }}>
              Drag the sample-mean slider.<br/>
              If μ₀ falls outside the blue whiskers, the orange line sits outside the
              interval → we reject H₀.
            </p>
          </>
        )
      }
  ],
  
  
  
    /* ----------------------------------------------------------
       CHAPTER 10: Intro to Calculus (Enrichment)
    ---------------------------------------------------------- */
    /* ---------------- Grade 12 ‧ Chapter 10: Intro to Calculus -------------- */
10: [
    /* ─────────────────────────────────────────────────────────────────── */
    {
      title: <h1>Intro to Calculus — Slide 1: What Is a Limit?</h1>,
      body: (
        <>
          <p>
            A <strong>limit</strong> formalises “getting arbitrarily close.”
            We write&nbsp;
            <code>lim&nbsp;(x→a)&nbsp;f(x)&nbsp;=&nbsp;L</code>&nbsp;if we can make
            <em>f(x)</em> as near as we like to <em>L</em> by taking
            <em>x</em> sufficiently near <em>a</em>.
          </p>
  
          <LimitExplorerTool
            expr="(x**2 - 1)/(x - 1)"
            a={1}
          />
  
          <p style={{ fontStyle:"italic" }}>
            For <code>(x²−1)/(x−1)</code> the limit at x→1 is 2 even though the function
            itself is undefined at 1 — a removable discontinuity.
          </p>
        </>
      )
    },
  
    /* ─────────────────────────────────────────────────────────────────── */
    {
      title: <h1>Intro to Calculus — Slide 2: Derivative as a Limit</h1>,
      body: (
        <>
          <p>
            The <strong>derivative</strong> is the limit of slopes of secant lines:
            <code>{`f'(x)= lim_{h→0} (f(x+h) − f(x))/h`}</code>.
          </p>
  
          <TangentSlopeTool expr="x**3" xmin={-3} xmax={3} />
  
          <p style={{ fontSize:24, fontStyle:"italic" }}>
            Drag <em>x₀</em>: the orange tangent line updates and the slope read-out
            matches the analytic derivative <code>f'(x)=3x²</code>.
          </p>
        </>
      )
    },
  
    /* ─────────────────────────────────────────────────────────────────── */
    {
      title: <h1>Intro to Calculus — Slide 3: Quick-Fire Differentiation Rules</h1>,
      body: (
        <>
          <ul>
            <li><strong>Power rule:</strong> <code>d/dx (xⁿ) = n xⁿ⁻¹</code></li>
            <li><strong>Product rule:</strong> <code>(fg)' = f'g + fg'</code></li>
            <li><strong>Chain rule:</strong> <code>(f∘g)' = (f'∘g)·g'</code></li>
          </ul>
          <p>
            <em>Examples</em>:
            <br/><code>f(x)=x² → f'(x)=2x</code>
            <br/><code>g(x)=3x³−4x → g'(x)=9x²−4</code>
            <br/><code>h(x)=sin(x²) → h'(x)=2x·cos(x²)</code>
          </p>
          <p>
            Derivatives locate maxima/minima (set f'(x)=0) and model rates (velocity,
            growth, etc.).
          </p>
        </>
      )
    },
  
    /* ─────────────────────────────────────────────────────────────────── */
    {
      title: <h1>Intro to Calculus — Slide 4: Why Limits Still Matter</h1>,
      body: (
        <>
          <p>
            Beyond derivatives, limits decide
            <em> continuity</em>,
            <em> asymptotes</em>,
            and <em>infinite series</em> convergence
            (<code>lim&nbsp;(n→∞)&nbsp;aₙ</code>).
            Mastery of limits underpins every technique in full calculus.
          </p>
  
          {/* optional: reuse LimitExplorerTool for an oscillating example */}
          <LimitExplorerTool expr="sin(1/x)" a={0} />
  
          <p style={{ fontStyle:"italic" }}>
            <code>sin(1/x)</code> near x=0 never settles → the limit does not exist,
            illustrating why rigorous ε–δ definitions matter.
          </p>
        </>
      )
    }
  ]
}